import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  PostMessagePayloadInit,
  PostMessagePayloadPageChange,
  OutboundPostMessage,
  WidgetMode,
} from '../types';

/**
 * Security model for postMessage communication:
 *
 * INBOUND (parent -> iframe): We cannot strictly validate the parent's origin
 * because the widget may be embedded on any customer domain. Instead, we only
 * process a whitelist of known `qbrains:` message types and ignore everything
 * else. This limits the attack surface to the specific actions those message
 * types trigger (setting page context / identity), which are non-privileged.
 *
 * OUTBOUND (iframe -> parent): We send with targetOrigin '*' because we don't
 * know the embedding page's origin. To mitigate risk, OutboundPostMessage is
 * restricted to non-sensitive data only (ready signal, unread count, close,
 * minimize, resize, sound). Tokens, message content, and PII are NEVER sent
 * to the parent window.
 */

/** Exhaustive set of inbound message types we accept from the parent. */
const ALLOWED_INBOUND_TYPES = new Set([
  'qbrains:init',
  'qbrains:open',
  'qbrains:close',
  'qbrains:page_change',
]);

interface UsePostMessageReturn {
  pageContext: PostMessagePayloadInit['pageContext'];
  identity: PostMessagePayloadInit['identity'] | null;
  sendToParent: (message: OutboundPostMessage) => void;
}

// No-op sendToParent for standalone mode
const noop = () => {};

export function usePostMessage(widgetMode: WidgetMode): UsePostMessageReturn {
  const [pageContext, setPageContext] = useState<PostMessagePayloadInit['pageContext']>(null);
  const [identity, setIdentity] = useState<PostMessagePayloadInit['identity'] | null>(
    // In standalone mode, return null identity immediately (no parent to receive from)
    null
  );
  const readySentRef = useRef(false);

  const sendToParent = useCallback((message: OutboundPostMessage) => {
    if (window.parent && window.parent !== window) {
      // Using '*' is acceptable here — OutboundPostMessage only carries
      // non-sensitive data (unread count, close, resize). Never send tokens
      // or message content to the parent.
      window.parent.postMessage(message, '*');
    }
  }, []);

  useEffect(() => {
    // In standalone mode, skip postMessage setup entirely — no parent iframe to talk to
    if (widgetMode === 'standalone') return;

    // Signal ready to parent on mount
    if (!readySentRef.current) {
      readySentRef.current = true;
      sendToParent({ type: 'qbrains:ready' });
    }

    function handleMessage(event: MessageEvent) {
      const data = event.data;
      if (!data || typeof data.type !== 'string') return;

      // Only process known qbrains message types — reject everything else
      if (!ALLOWED_INBOUND_TYPES.has(data.type)) return;

      switch (data.type) {
        case 'qbrains:init': {
          const payload = data.payload as PostMessagePayloadInit;
          if (payload.pageContext) {
            setPageContext(payload.pageContext);
          }
          if (payload.identity) {
            setIdentity(payload.identity);
          }
          break;
        }
        case 'qbrains:page_change': {
          const payload = data.payload as PostMessagePayloadPageChange;
          setPageContext((prev) =>
            prev ? { ...prev, url: payload.url, pageTitle: payload.pageTitle } : null
          );
          break;
        }
        case 'qbrains:open':
          // Widget is now visible -- app can respond if needed
          break;
        case 'qbrains:close':
          // Parent requested close
          break;
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendToParent, widgetMode]);

  // In standalone mode, sendToParent is a no-op
  return {
    pageContext,
    identity,
    sendToParent: widgetMode === 'standalone' ? noop : sendToParent,
  };
}
